import { REALTIME_ORIGIN_HEADER } from "@birdwatch/common";

import { REALTIME_ORIGIN } from "./realtime-origin";

/**
 * Platform Request Headers
 *
 * Single home for the headers every `@birdwatch/client` call carries, so the six
 * `fetch` sites across the hooks cannot drift apart.
 */

/**
 * Builds the headers for a platform REST v5 request.
 *
 * `X-Request-From` is how the platform tells its clients apart: the native apps send
 * `owner_app` / `field_app`, so the webapp declaring `web_app` is what keeps a message
 * posted from a phone *browser* recorded as `WEBAPP` rather than `MOBILE`
 * (`platform/src/lib/utils/utils.ts` `isMobile`). Without it the platform has nothing
 * to go on but the `User-Agent`, and guesses.
 *
 * `X-Realtime-Origin` names this tab, so the realtime events the write causes come
 * back marked as this tab's own echo (see realtime-origin.ts).
 *
 * @param token - The Clerk session token, or `null` when signed out. Public pages send
 *   no `Authorization` header and their platform route declares `auth: { required: false }`.
 * @param extraHeaders - The caller's own headers (e.g. Smart Action attribution). They
 *   never replace the headers above: one with the same name, in any letter case, is dropped.
 * @returns The header map to hand to `fetch`.
 */
export function buildRequestHeaders(
  token: string | null,
  extraHeaders: Record<string, string> = {},
): Record<string, string> {
  const ownHeaders: Record<string, string> = {
    "Content-Type": "application/json",
    "X-Request-From": "web_app",
    [REALTIME_ORIGIN_HEADER]: REALTIME_ORIGIN,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
  // Header names are case-insensitive: a caller's `content-type` would be sent
  // next to ours rather than replaced. `Authorization` comes only from the session
  // token, so it stays reserved when signed out too.
  const ownNames = new Set(
    ["Authorization", ...Object.keys(ownHeaders)].map((name) =>
      name.toLowerCase(),
    ),
  );
  const callerHeaders = Object.entries(extraHeaders).filter(
    ([name]) => !ownNames.has(name.toLowerCase()),
  );
  return { ...Object.fromEntries(callerHeaders), ...ownHeaders };
}
